import { jsx, jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "@inbody/core";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { EMPTY_VALUE } from "../constants.js";
import { useOrgTree } from "../hooks/useOrgTree.js";
import { getGroupCrumbList } from "../utils/orgPath.js";
import { getOrgCrumbList } from "../utils/orgTree.js";
function OrgBreadcrumb(props) {
  const { user, getOrgGroupHref, onSelectOrgGroup, orgLinkLabel } = props;
  const { t } = useTranslation("ui");
  const { gray, brand } = useColors();
  const { orgTree } = useOrgTree();
  const crumbList = useMemo(() => {
    const treeCrumbList = getOrgCrumbList(orgTree, user.id);
    return treeCrumbList.length ? treeCrumbList : getGroupCrumbList(user.leafGroupPath);
  }, [orgTree, user]);
  const linkTargetName = orgLinkLabel || t("조직도");
  const hasLink = crumbList.some(
    (crumb) => Boolean(getOrgGroupHref == null ? void 0 : getOrgGroupHref(crumb)) || Boolean(onSelectOrgGroup)
  );
  if (!crumbList.length) {
    return /* @__PURE__ */ jsx(Typography, { variant: "caption", color: gray[500], children: EMPTY_VALUE });
  }
  return /* @__PURE__ */ jsxs(Stack, { spacing: 0.5, minWidth: 0, children: [
    /* @__PURE__ */ jsx(
      Breadcrumbs,
      {
        separator: /* @__PURE__ */ jsx(NavigateNextIcon, { sx: { fontSize: 14 } }),
        sx: {
          "& .MuiBreadcrumbs-separator": { mx: 0.5, color: gray[300] },
          "& .MuiBreadcrumbs-ol": { rowGap: 0.25 }
        },
        children: crumbList.map((crumb, index) => {
          const isLeaf = index === crumbList.length - 1;
          const href = getOrgGroupHref == null ? void 0 : getOrgGroupHref(crumb);
          const clickable = Boolean(href || onSelectOrgGroup);
          const isExternalHref = (href == null ? void 0 : href.startsWith("http")) ?? false;
          const textSx = {
            color: isLeaf ? gray[700] : gray[500],
            fontWeight: isLeaf ? 600 : 400
          };
          if (!clickable) {
            return /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "caption",
                noWrap: true,
                sx: textSx,
                children: crumb.name
              },
              crumb.id ?? crumb.groupPath
            );
          }
          return /* @__PURE__ */ jsx(
            Tooltip,
            {
              title: t("{{name}}에서 보기", { name: linkTargetName }),
              placement: "top",
              children: /* @__PURE__ */ jsx(
                Link,
                {
                  href,
                  component: href ? "a" : "button",
                  type: href ? void 0 : "button",
                  target: isExternalHref ? "_blank" : void 0,
                  rel: isExternalHref ? "noopener noreferrer" : void 0,
                  underline: "none",
                  variant: "caption",
                  onClick: onSelectOrgGroup ? (event) => {
                    if (!href) {
                      event.preventDefault();
                    }
                    onSelectOrgGroup(crumb);
                  } : void 0,
                  sx: {
                    ...textSx,
                    p: 0,
                    border: "none",
                    bgcolor: "transparent",
                    cursor: "pointer",
                    // 눌러 볼 수 있는 글자라는 걸 점선 밑줄로 먼저 알린다.
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                    textDecorationColor: gray[300],
                    textUnderlineOffset: "3px",
                    "&:hover": {
                      color: brand[600],
                      textDecorationStyle: "solid",
                      textDecorationColor: brand[600]
                    }
                  },
                  children: crumb.name
                }
              )
            },
            crumb.id ?? crumb.groupPath
          );
        })
      }
    ),
    hasLink && // 어디로 가는 링크인지 한 줄로 못 박아 둔다.
    /* @__PURE__ */ jsxs(
      Stack,
      {
        direction: "row",
        spacing: 0.5,
        alignItems: "center",
        sx: {
          alignSelf: "flex-start",
          px: 0.75,
          py: 0.25,
          borderRadius: 999,
          bgcolor: gray[25],
          color: gray[500]
        },
        children: [
          /* @__PURE__ */ jsx(OpenInNewIcon, { sx: { fontSize: 12 } }),
          /* @__PURE__ */ jsx(Box, { component: "span", sx: { minWidth: 0 }, children: /* @__PURE__ */ jsx(Typography, { variant: "small", noWrap: true, children: t("클릭하면 {{name}}로 이동합니다", { name: linkTargetName }) }) })
        ]
      }
    )
  ] });
}
export {
  OrgBreadcrumb
};
